import { memo } from 'react';
import { Handle, Position, useReactFlow, useNodeConnections } from '@xyflow/react';
import { EcaInlineField } from '../EcaInlineField.jsx';
import { useCatalogue } from '../EcaPluginCatalogueContext.jsx';
import { useNodeHover, BottomHandleLabels, nodeShell, NODE_COLORS, useZoomLod, NodeLodOverlay } from '../EcaHandleLabel.jsx';
import { getNodeIcon } from '../ecaNodeVisuals.js';

/**
 * Event node — trigger of the ECA workflow.
 *
 * Axes:
 *   - No LEFT handle (events have no flux input; they ARE the source)
 *   - RIGHT handle: flux output (blue)
 *   - TOP handles: data inputs for wirable config fields (green) — none for events typically
 *   - BOTTOM handles: data token outputs (entity, account, etc.) (green)
 */
export const EcaEventNode = memo(function EcaEventNode({ id, data, selected }) {
  const { updateNodeData } = useReactFlow();
  const { hovered, hoverProps } = useNodeHover();
  const lod = useZoomLod();
  // Node-scoped subscription: re-renders only when THIS node's connections change.
  const connections = useNodeConnections({ id });
  const { getPluginDef } = useCatalogue();
  const pluginDef = getPluginDef('events', data.plugin);
  const tokenOutputs = pluginDef?.tokenOutputs ?? [];
  const configSchema = pluginDef?.configSchema ?? [];
  const wirableFields = configSchema.filter((f) => f.wirable);

  // Keys of handles that have a live edge — their labels stay visible without hover.
  // Events have no wirable data inputs (no TopHandleLabels), so only bottom keys.
  const connectedBottomKeys = new Set(
    connections.filter(c => c.source === id && c.sourceHandle?.startsWith('data-out-'))
      .map(c => c.sourceHandle.replace('data-out-', ''))
  );

  const updateConfig = (key, value) =>
    updateNodeData(id, { configuration: { ...data.configuration, [key]: value } });

  return (
    // Outer wrapper: overflow visible so hover labels can float outside
    <div {...hoverProps} style={{ overflow: 'visible', position: 'relative' }}>

      {/* TOP data input handles (wirable fields) */}
      {wirableFields.map((field, i) => (
        <Handle
          key={`data-in-${field.key}`}
          type="target"
          position={Position.Top}
          id={`data-in-${field.key}`}
          style={{ left: `${((i + 1) / (wirableFields.length + 1)) * 100}%`, background: '#10B981', border: '2px solid #059669', width: 10, height: 10 }}
        />
      ))}
      {/* RIGHT flux output handle */}
      <Handle type="source" position={Position.Right} id="flux-out"
        style={{ background: '#3B82F6', border: '2px solid #1D4ED8', width: 12, height: 12 }} />

      {/* BOTTOM data output handles — on the border, like TOP handles */}
      {tokenOutputs.map((token, i) => (
        <Handle
          key={`data-out-${token}`}
          type="source"
          position={Position.Bottom}
          id={`data-out-${token}`}
          style={{ left: `${((i + 1) / (tokenOutputs.length + 1)) * 100}%`, background: '#10B981', border: '2px solid #059669', width: 10, height: 10 }}
        />
      ))}

      {/* Hover labels — also shown when the handle is connected */}
      <BottomHandleLabels items={tokenOutputs.map(t => ({ key: t, label: t }))} hovered={hovered} connectedKeys={connectedBottomKeys} />

      {/* Visual shell — box-shadow border avoids sub-pixel gap at any zoom level */}
      <div style={{ ...nodeShell(NODE_COLORS.event, selected), opacity: lod ? 0 : 1, transition: 'opacity 160ms ease' }}>
        {/* Header */}
        <div className="bg-iris-600 text-white px-3 py-1.5">
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold uppercase tracking-wider opacity-70 shrink-0">Event</span>
            <span className="text-xs font-medium truncate">{data.label}</span>
          </div>
          {data.description && (
            <div className="text-xs text-white truncate mt-0.5">{data.description}</div>
          )}
        </div>

        {/* Plugin badge */}
        <div className="px-3 pt-2 pb-1">
          <span className="text-xs text-iris-600 bg-iris-50 px-2 py-0.5 rounded border border-iris-100 font-mono">
            {data.plugin}
          </span>
        </div>

        {/* Config fields */}
        <div className="px-3 pb-2 space-y-1">
          {configSchema.map((field) => (
            <EcaInlineField
              key={field.key}
              nodeId={id}
              fieldKey={field.key}
              label={field.label}
              value={data.configuration?.[field.key] ?? ''}
              type={field.type}
              options={field.options}
              placeholder={field.placeholder}
              canBeWired={field.wirable}
              onChange={(val) => updateConfig(field.key, val)}
            />
          ))}
        </div>
      </div>

      {/* Low-zoom billboard */}
      <NodeLodOverlay
        active={lod} colors={NODE_COLORS.event} selected={selected}
        icon={getNodeIcon('event', data.plugin)}
        title={data.label}
      />
    </div>
  );
});
